import { StateCreator } from 'zustand';
import type { CarryState, Character, Equipment, Weapon, Armor } from '../../../types/character';
import type { JamType } from '../../../types/jam';
import { CyberneticsService } from '../../../services/CyberneticsService';
import { AmmunitionService } from '../../../services/AmmunitionService';
import {
  canWield,
  checkTransition,
  COUNTER_FOR_ACTION_COST,
  type ActionCost,
  type TransitionResult
} from '../../../services/CarryStateService';
import type { ActionCounters } from '../../../types/combatRound';

/**
 * The slice reads two fields it does not own, from the combat-round slice mounted in
 * the same store. Optional so the equipment actions still work when the slice is
 * exercised on its own.
 */
type Get = () => {
  character: Character | null;
  isInCombat?: boolean;
  incrementActionCounter?: (kind: keyof ActionCounters) => void;
};
type Set = (partial: { character: Character; hasUnsavedChanges: boolean }) => void;

/**
 * Record a transition's cost against the round budget.
 *
 * Only while in combat: out of structured time there is no action economy to spend.
 * Only on success: a refused transition changed nothing, so it costs nothing.
 *
 * This counts what the player did — it never blocks. Combat actions are voluntary,
 * and a table that ignores the tracker is using it correctly, so exceeding a budget
 * shows as a warning chip in `CombatRoundBar` rather than a refusal.
 */
function chargeActionCost(get: Get, cost: ActionCost): void {
  const state = get();
  if (!state.isInCombat) return;
  const counter = COUNTER_FOR_ACTION_COST[cost];
  if (!counter) return;
  state.incrementActionCounter?.(counter);
}

/**
 * Apply a carry-state change to a weapon after validating it.
 *
 * Dropping clears the hand assignment but deliberately preserves `jamState` — a
 * jammed weapon on the floor is still jammed.
 */
function transitionWeapon(get: Get, set: Set, weaponId: string, to: CarryState): TransitionResult {
  const { character } = get();
  if (!character) return { ok: false, reason: 'invalid-state', from: 'owned', to };

  const weapon = character.weapons.find((w) => w.id === weaponId);
  if (!weapon) return { ok: false, reason: 'invalid-state', from: 'owned', to };

  const legality = checkTransition(weapon.carryState, to);
  if (!legality.ok) return legality;

  if (to === 'wielded') {
    const budget = canWield(weapon, character.weapons, character.equipment);
    if (!budget.ok) return budget;
  }

  set({
    character: {
      ...character,
      weapons: character.weapons.map((w) =>
        w.id === weaponId
          ? {
              ...w,
              carryState: to,
              // Hands are only occupied while wielded.
              ...(to !== 'wielded' && { handAssignment: 'unassigned' as const })
            }
          : w
      ),
      lastModified: new Date()
    },
    hasUnsavedChanges: true
  });
  chargeActionCost(get, legality.actionCost);
  return legality;
}

/** Apply a carry-state change to a general equipment item (stash / retrieve). */
function transitionItem(get: Get, set: Set, itemId: string, to: CarryState): TransitionResult {
  const { character } = get();
  if (!character) return { ok: false, reason: 'invalid-state', from: 'owned', to };

  const item = character.equipment.find((eq) => eq.id === itemId);
  if (!item) return { ok: false, reason: 'invalid-state', from: 'owned', to };

  const legality = checkTransition(item.carryState, to);
  if (!legality.ok) return legality;

  set({
    character: {
      ...character,
      equipment: character.equipment.map((eq) =>
        eq.id === itemId ? { ...eq, carryState: to } : eq
      ),
      lastModified: new Date()
    },
    hasUnsavedChanges: true
  });
  chargeActionCost(get, legality.actionCost);
  return legality;
}

export interface EquipmentSlice {
  addEquipment: (equipment: Equipment) => void;
  removeEquipment: (equipmentId: string) => void;
  /** Cybernetics only: fit or remove an implant. */
  toggleCyberneticInstalled: (equipmentId: string) => void;
  /** Carry-state transitions. Each returns success plus the action cost, or a refusal. */
  drawWeapon: (weaponId: string) => TransitionResult;
  stowWeapon: (weaponId: string) => TransitionResult;
  dropWeapon: (weaponId: string) => TransitionResult;
  recoverWeapon: (weaponId: string, to?: 'wielded' | 'carried') => TransitionResult;
  stashItem: (itemId: string) => TransitionResult;
  retrieveItem: (itemId: string) => TransitionResult;
  /** Persisted jam state. */
  setWeaponJammed: (weaponId: string, type: JamType) => void;
  clearWeaponJam: (weaponId: string) => void;
  updateEquipmentNotes: (
    equipmentId: string,
    customName: string,
    customDescription: string
  ) => void;
  addWeapon: (weapon: Weapon) => void;
  removeWeapon: (weaponId: string) => void;
  addArmor: (armor: Armor) => void;
  removeArmor: (armorId: string) => void;
  /** Armour only: don or remove. Worn implies carried. */
  toggleArmorWorn: (armorId: string) => void;
  consumeAmmo: (weaponId: string, amount: number) => void;
  reloadWeapon: (weaponId: string) => void;
  tickReload: (weaponId: string) => void;
  tickRecharge: (weaponId: string) => void;
  setAmmoType: (weaponId: string, ammoType: string) => void;
  loadAmmo: (weaponId: string, ammoId: string | null) => void;
  consumeUnusualAmmo: (ammoId: string, amount: number) => void;
  addAmmoToInventory: (ammoId: string, quantity: number) => void;
  addConsumableWeapon: (weapon: Weapon, quantity: number) => void;
  addConsumableEquipment: (equipment: Equipment, quantity: number) => void;
}

interface CharacterStore extends EquipmentSlice {
  character: Character | null;
  hasUnsavedChanges: boolean;
}

export const createEquipmentSlice: StateCreator<CharacterStore, [], [], EquipmentSlice> = (
  set,
  get
) => ({
  addEquipment: (equipment: Equipment) => {
    const { character } = get();
    if (!character) return;

    // Pre-populate installedLocation for cybernetics from definition defaults
    const defaultLocation = CyberneticsService.getDefaultLocation(equipment.sourceId);
    const equipmentToAdd =
      defaultLocation && !equipment.installedLocation
        ? { ...equipment, installedLocation: defaultLocation }
        : equipment;

    const updatedCharacter = {
      ...character,
      equipment: [...character.equipment, equipmentToAdd],
      lastModified: new Date()
    };

    try {
      localStorage.setItem('rogue-trader-current-character', JSON.stringify(updatedCharacter));
    } catch (error) {
      console.warn('Failed to auto-save character to localStorage:', error);
    }

    set({
      character: updatedCharacter,
      hasUnsavedChanges: true
    });
  },

  removeEquipment: (equipmentId: string) => {
    const { character } = get();
    if (!character) return;

    const removedItem = character.equipment.find((eq) => eq.id === equipmentId);
    const updatedEquipment = character.equipment.filter((eq) => eq.id !== equipmentId);

    // If the removed item was a cybernetic, clean up its effects
    if (removedItem && CyberneticsService.isCybernetic(removedItem)) {
      // Recompute conditional modifiers: remove cybernetic-sourced ones, recalculate from remaining
      const nonCyberneticModifiers = (character.conditionalModifiers ?? []).filter(
        (mod) => !mod.source.startsWith('Cybernetic: ')
      );
      const cyberneticModifiers = CyberneticsService.getConditionalModifiers(
        updatedEquipment,
        character.skills
      );

      // Recompute virtual talents: remove cybernetic-sourced ones, recalculate from remaining
      const nonCyberneticTalents = (character.talents ?? []).filter(
        (t) => !t.source?.startsWith('Cybernetic: ')
      );
      const virtualTalents = CyberneticsService.getVirtualTalents(
        updatedEquipment,
        nonCyberneticTalents
      );
      const convertedTalents = virtualTalents.map((vt) => ({
        id: vt.talentId,
        name: vt.talentName,
        specialization: vt.specialization,
        source: vt.source,
        tier: 1,
        prerequisites: [] as string[],
        shortDescription: '',
        fullDescription: '',
        rulebook: 'cybernetic',
        purchaseCount: 0,
        purchaseHistory: []
      }));

      set({
        character: {
          ...character,
          equipment: updatedEquipment,
          conditionalModifiers: [...nonCyberneticModifiers, ...cyberneticModifiers],
          talents: [...nonCyberneticTalents, ...convertedTalents],
          lastModified: new Date()
        },
        hasUnsavedChanges: true
      });
    } else {
      set({
        character: {
          ...character,
          equipment: updatedEquipment,
          lastModified: new Date()
        },
        hasUnsavedChanges: true
      });
    }
  },

  toggleCyberneticInstalled: (equipmentId: string) => {
    const { character } = get();
    if (!character) return;

    const item = character.equipment.find((eq) => eq.id === equipmentId);
    if (!item) return;

    const isEquipping = !item.installed;

    // Mounting limit validation: if equipping a mechadendrite, check limit first
    if (isEquipping && CyberneticsService.isMechadendrite(item)) {
      const toughness = character.characteristics.toughness;
      // Build a prospective equipment list with this item equipped
      const prospectiveEquipment = character.equipment.map((eq) =>
        eq.id === equipmentId ? { ...eq, installed: true } : eq
      );
      const { isValid, max } = CyberneticsService.validateMountingLimit(
        prospectiveEquipment,
        toughness
      );
      if (!isValid) {
        const tb = CyberneticsService.getMaxMechadendrites(toughness);
        console.warn(`Cannot equip: maximum ${max} mechadendrites (Toughness Bonus ${tb})`);
        return;
      }
    }

    // Commit the toggle
    const updatedEquipment = character.equipment.map((eq) =>
      eq.id === equipmentId ? { ...eq, installed: !eq.installed } : eq
    );

    // Recompute cybernetic conditional modifiers (covers all cybernetics including mechadendrites)
    const nonCyberneticModifiers = (character.conditionalModifiers ?? []).filter(
      (mod) => !mod.source.startsWith('Cybernetic: ')
    );
    const cyberneticModifiers = CyberneticsService.getConditionalModifiers(
      updatedEquipment,
      character.skills
    );

    // Virtual talent management: remove old cybernetic talents, add current ones
    const nonCyberneticTalents = (character.talents ?? []).filter(
      (t) => !t.source?.startsWith('Cybernetic: ')
    );
    const virtualTalents = CyberneticsService.getVirtualTalents(
      updatedEquipment,
      nonCyberneticTalents
    );
    const convertedTalents = virtualTalents.map((vt) => ({
      id: vt.talentId,
      name: vt.talentName,
      specialization: vt.specialization,
      source: vt.source,
      tier: 1,
      prerequisites: [] as string[],
      shortDescription: '',
      fullDescription: '',
      rulebook: 'cybernetic',
      purchaseCount: 0,
      purchaseHistory: []
    }));

    set({
      character: {
        ...character,
        equipment: updatedEquipment,
        conditionalModifiers: [...nonCyberneticModifiers, ...cyberneticModifiers],
        talents: [...nonCyberneticTalents, ...convertedTalents],
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  updateEquipmentNotes: (equipmentId: string, customName: string, customDescription: string) => {
    const { character } = get();
    if (!character) return;

    // Check all three arrays: equipment, weapons, armor
    const inEquipment = character.equipment.some((eq) => eq.id === equipmentId);
    const inWeapons = character.weapons.some((w) => w.id === equipmentId);
    const inArmor = character.armor.some((a) => a.id === equipmentId);

    const notes = {
      customName: customName.trim() || undefined,
      customDescription: customDescription.trim() || undefined
    };

    const updates: Partial<Character> = { lastModified: new Date() };

    if (inEquipment) {
      updates.equipment = character.equipment.map((eq) =>
        eq.id === equipmentId ? { ...eq, ...notes } : eq
      );
    }
    if (inWeapons) {
      updates.weapons = character.weapons.map((w) =>
        w.id === equipmentId ? { ...w, ...notes } : w
      );
    }
    if (inArmor) {
      updates.armor = character.armor.map((a) => (a.id === equipmentId ? { ...a, ...notes } : a));
    }

    set({
      character: { ...character, ...updates },
      hasUnsavedChanges: true
    });
  },

  addWeapon: (weapon: Weapon) => {
    const { character } = get();
    if (!character) return;

    const updatedCharacter = {
      ...character,
      weapons: [...character.weapons, weapon],
      lastModified: new Date()
    };

    try {
      localStorage.setItem('rogue-trader-current-character', JSON.stringify(updatedCharacter));
    } catch (error) {
      console.warn('Failed to auto-save character to localStorage:', error);
    }

    set({
      character: updatedCharacter,
      hasUnsavedChanges: true
    });
  },

  removeWeapon: (weaponId: string) => {
    const { character } = get();
    if (!character) return;

    set({
      character: {
        ...character,
        weapons: character.weapons.filter((weapon) => weapon.id !== weaponId),
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  // ── Carry-state transitions ─────────────────────────────────────────────
  // Each validates first and returns the action cost, so callers in structured
  // time can charge it. Validation lives here, never in a render effect.

  drawWeapon: (weaponId: string) => transitionWeapon(get, set, weaponId, 'wielded'),
  stowWeapon: (weaponId: string) => transitionWeapon(get, set, weaponId, 'carried'),
  dropWeapon: (weaponId: string) => transitionWeapon(get, set, weaponId, 'dropped'),
  recoverWeapon: (weaponId: string, to: 'wielded' | 'carried' = 'wielded') =>
    transitionWeapon(get, set, weaponId, to),
  stashItem: (itemId: string) => transitionItem(get, set, itemId, 'owned'),
  retrieveItem: (itemId: string) => transitionItem(get, set, itemId, 'carried'),

  setWeaponJammed: (weaponId: string, type: JamType) => {
    const { character } = get();
    if (!character) return;
    set({
      character: {
        ...character,
        weapons: character.weapons.map((weapon) =>
          weapon.id === weaponId ? { ...weapon, jamState: { jammed: true, type } } : weapon
        ),
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  clearWeaponJam: (weaponId: string) => {
    const { character } = get();
    if (!character) return;
    set({
      character: {
        ...character,
        weapons: character.weapons.map((weapon) =>
          weapon.id === weaponId ? { ...weapon, jamState: undefined } : weapon
        ),
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  addArmor: (armor: Armor) => {
    const { character } = get();
    if (!character) return;

    set({
      character: {
        ...character,
        armor: [...character.armor, armor],
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  removeArmor: (armorId: string) => {
    const { character } = get();
    if (!character) return;

    set({
      character: {
        ...character,
        armor: character.armor.filter((armor) => armor.id !== armorId),
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  toggleArmorWorn: (armorId: string) => {
    const { character } = get();
    if (!character) return;

    set({
      character: {
        ...character,
        armor: character.armor.map((armor) =>
          armor.id === armorId ? { ...armor, worn: !armor.worn } : armor
        ),
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  /**
   * Add a consumable weapon (a grenade), stacking onto an existing entry.
   *
   * Buying three frag grenades should give one entry holding three, not three entries:
   * the stock displayed as "3/3" then comes from the inventory rather than a fixed
   * loadout, and throwing one decrements it. Restocking from ship's stores is another
   * purchase, which stacks again.
   *
   * Stacks only when craftsmanship matches, since a Good grenade is not interchangeable
   * with a Common one.
   */
  addConsumableWeapon: (weapon: Weapon, quantity: number) => {
    const { character } = get();
    if (!character) return;

    const existing = (character.weapons ?? []).find(
      (w) => w.sourceId === weapon.sourceId && w.craftsmanship === weapon.craftsmanship
    );

    const weapons = existing
      ? character.weapons.map((w) =>
          w.id === existing.id
            ? {
                ...w,
                clip: (typeof w.clip === 'number' ? w.clip : 0) + quantity,
                currentClip: (w.currentClip ?? (typeof w.clip === 'number' ? w.clip : 0)) + quantity
              }
            : w
        )
      : [...(character.weapons ?? []), { ...weapon, clip: quantity, currentClip: quantity }];

    set({
      character: { ...character, weapons, lastModified: new Date() },
      hasUnsavedChanges: true
    });
  },

  /**
   * Add consumable gear (a narcotic), stacking onto an existing entry.
   *
   * The gear counterpart of addConsumableWeapon: a grenade stacks on `clip` because it
   * is a weapon, a narcotic stacks on `quantity` because it is not. Stacks only when
   * craftsmanship matches.
   */
  addConsumableEquipment: (equipment: Equipment, quantity: number) => {
    const { character } = get();
    if (!character) return;

    const existing = (character.equipment ?? []).find(
      (e) => e.sourceId === equipment.sourceId && e.craftsmanship === equipment.craftsmanship
    );

    const next = existing
      ? character.equipment.map((e) =>
          e.id === existing.id ? { ...e, quantity: (e.quantity ?? 1) + quantity } : e
        )
      : [...(character.equipment ?? []), { ...equipment, quantity }];

    set({
      character: { ...character, equipment: next, lastModified: new Date() },
      hasUnsavedChanges: true
    });
  },

  consumeAmmo: (weaponId: string, amount: number) => {
    const { character } = get();
    if (!character) return;

    set({
      character: {
        ...character,
        weapons: character.weapons.map((w) => {
          if (w.id !== weaponId) return w;
          const current = w.currentClip ?? w.clip;
          return { ...w, currentClip: Math.max(0, current - amount) };
        }),
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  reloadWeapon: (weaponId: string) => {
    const { character } = get();
    if (!character) return;

    const weapon = character.weapons.find((w) => w.id === weaponId);
    if (!weapon) return;

    // Use structured reloadActions field
    const roundsNeeded = weapon.reloadActions ?? 0;

    if (roundsNeeded > 1) {
      // Multi-round reload: set counter, don't fill clip yet
      set({
        character: {
          ...character,
          weapons: character.weapons.map((w) =>
            w.id === weaponId ? { ...w, reloadRoundsLeft: roundsNeeded - 1 } : w
          ),
          lastModified: new Date()
        },
        hasUnsavedChanges: true
      });
    } else {
      // Instant reload (Half or Full action)
      set({
        character: {
          ...character,
          weapons: character.weapons.map((w) =>
            w.id === weaponId ? { ...w, currentClip: w.clip, reloadRoundsLeft: 0 } : w
          ),
          lastModified: new Date()
        },
        hasUnsavedChanges: true
      });
    }
  },

  tickReload: (weaponId: string) => {
    const { character } = get();
    if (!character) return;

    set({
      character: {
        ...character,
        weapons: character.weapons.map((w) => {
          if (w.id !== weaponId || !w.reloadRoundsLeft) return w;
          if (w.reloadRoundsLeft <= 1) {
            return { ...w, currentClip: w.clip, reloadRoundsLeft: 0 };
          }
          return { ...w, reloadRoundsLeft: w.reloadRoundsLeft - 1 };
        }),
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  tickRecharge: (weaponId: string) => {
    const { character } = get();
    if (!character) return;

    set({
      character: {
        ...character,
        weapons: character.weapons.map((w) => {
          if (w.id !== weaponId) return w;
          return { ...w, rechargeRoundsLeft: Math.max(0, (w.rechargeRoundsLeft || 0) - 1) };
        }),
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  setAmmoType: (weaponId: string, ammoType: string) => {
    const { character } = get();
    if (!character) return;

    set({
      character: {
        ...character,
        weapons: character.weapons.map((w) => (w.id === weaponId ? { ...w, ammoType } : w)),
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  loadAmmo: (weaponId: string, ammoId: string | null) => {
    const { character } = get();
    if (!character) return;

    const weapon = character.weapons.find((w) => w.id === weaponId);
    if (!weapon) {
      console.warn(`loadAmmo: weapon "${weaponId}" not found`);
      return;
    }

    if (ammoId !== null) {
      const def = AmmunitionService.getDefinition(ammoId);
      if (!def) {
        console.warn(`loadAmmo: ammo definition "${ammoId}" not found`);
        return;
      }
      if (!AmmunitionService.isCompatible(ammoId, weapon.sourceId)) {
        console.warn(
          `loadAmmo: ammo "${ammoId}" is not compatible with weapon "${weapon.sourceId}"`
        );
        return;
      }
    }

    set({
      character: {
        ...character,
        weapons: character.weapons.map((w) =>
          w.id === weaponId ? { ...w, loadedAmmoId: ammoId ?? undefined } : w
        ),
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  consumeUnusualAmmo: (ammoId: string, amount: number) => {
    const { character } = get();
    if (!character) return;

    const currentQty = character.ammoInventory?.[ammoId] ?? 0;
    const newQty = Math.max(0, currentQty - amount);

    set({
      character: {
        ...character,
        ammoInventory: {
          ...character.ammoInventory,
          [ammoId]: newQty
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  addAmmoToInventory: (ammoId: string, quantity: number) => {
    const { character } = get();
    if (!character) return;

    const currentQty = character.ammoInventory?.[ammoId] ?? 0;

    set({
      character: {
        ...character,
        ammoInventory: {
          ...character.ammoInventory,
          [ammoId]: currentQty + quantity
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  }
});
